import { useCallback, useMemo, useState } from "react";
import {
    ColumnMetadata,
    DataGrid,
    DataGridRow,
    RowSizes,
} from "~/components/DataGrid";
import { SectionHeader } from "~/components/SectionHeader";
import {
    ApiQuery,
    Filter,
    FilterMetadata,
    FilterOperators,
    PaginationState,
    QueryResponse,
    SortDirections,
    SortQuery,
} from "~/modules/filters/types";
import {
    APY_30D_COLUMN,
    ASSET_CLASS_COLUMN,
    NAME_COLUMN,
    NETWORKS_COLUMN,
    PCT_CHANGE_30D_COLUMN,
    PROTOCOL_COLUMN,
    VALUE_COLUMN,
    getRankColumn,
} from "./columns";
import { InfiniteScrollTracker } from "~/components/InfiniteScrollTracker";
import { FeatureNames } from "~/modules/feature_flag/flags";
import { FeatureFlag } from "~/modules/feature_flag/components/FeatureFlag";
import { DataExportProvider } from "~/modules/downloads/components/DataExportProvider";
import { DownloadButton } from "~/modules/downloads/components/DownloadButton";
import { ExportTableNames } from "~/modules/downloads/types";
import { Asset, AssetColumns } from "~/modules/assets/types";
import { DEFAULT_ASSET_LIST_QUERY } from "~/modules/assets/consts";
import { FilterFormContextProvider } from "~/modules/filters/components/FilterFormContext";
import { AssetScreenerFilters } from "~/modules/asset_screener/components/AssetScreenerFilters";
import { AssetScreenerListProvider } from "~/modules/asset_screener/components/AssetScreenerListProvider";
import { AssetScreenerStats } from "~/modules/asset_screener/api/queryAssetScreenerStats";
import {
    ASSET_SCREENER_BASE_FILTER,
    NON_KYC_FILTER_METADATA,
} from "~/modules/asset_screener/consts";
import { normalizeFilterOperators } from "~/modules/asset_screener/lib/normalizeFilterOperators";

// // // //

export interface GlobalAssetTableData {
    initialState: QueryResponse<Asset>;
    filterMetadata: FilterMetadata[];
    stats: AssetScreenerStats;
}

interface Props extends GlobalAssetTableData {
    showFilters?: boolean;
}

// Matches the sort of ASSET_SCREENER_DEFAULT_QUERY, which getStaticProps uses
// to fetch initialState - the two must stay in sync
const DEFAULT_SORT: SortQuery = {
    field: AssetColumns.bridgedTokenValueDollar,
    direction: SortDirections.desc,
};

/**
 * GlobalAssetTable
 * Renders the loaded asset screener results as a table, loading the next page
 * when the bottom of the table scrolls into view
 */
function GlobalAssetTable(props: {
    assets: Asset[];
    apiQuery: ApiQuery;
    onChangeQuery: (updatedQuery: ApiQuery) => void;
    fetching: boolean;
    hasNextPage: boolean;
    loadNextPage: () => void;
    pagination: PaginationState | undefined;
}) {
    const {
        assets,
        apiQuery,
        onChangeQuery,
        fetching,
        hasNextPage,
        loadNextPage,
        pagination,
    } = props;

    // Rank follows the API's sort order
    const columns: ColumnMetadata<Asset, AssetColumns>[] = useMemo(() => {
        const rankByAssetID = new Map(
            assets.map((asset, index) => [asset.id, index + 1]),
        );

        return [
            getRankColumn(rankByAssetID),
            NAME_COLUMN,
            PROTOCOL_COLUMN,
            ASSET_CLASS_COLUMN,
            VALUE_COLUMN,
            PCT_CHANGE_30D_COLUMN,
            APY_30D_COLUMN,
            NETWORKS_COLUMN,
        ];
    }, [assets]);

    if (assets.length === 0 && !fetching) {
        return (
            <div className="flex flex-col items-center justify-center gap-1 py-16 text-center">
                <p className="text-base text-secondary-foreground">
                    No assets found
                </p>
                <p className="text-sm text-tertiary-foreground">
                    Try adjusting your filters to see more results
                </p>
            </div>
        );
    }

    return (
        <>
            <DataGrid
                query={apiQuery}
                columns={columns}
                rows={assets}
                rowKey="id"
                bordered={false}
                size={RowSizes.small}
                loading={fetching}
                fixedHeader={true}
                analyticsTableName="asset-gigatable"
                paginationState={pagination}
                onChangeQuery={onChangeQuery}
                enableItemsPerPageSelect={false}
                enableScrollButton={false}
                disablePagination={true}
                flush={true}
                showHeader={true}
                fixedHeaderClassName="top-24"
            >
                {({ row, rowIndex, scrollLeft }) => (
                    <DataGridRow<Asset, AssetColumns>
                        row={row}
                        key={row.id}
                        columns={columns}
                        size={RowSizes.small}
                        bordered={false}
                        scrollLeft={scrollLeft}
                        striped={rowIndex % 2 === 1}
                    />
                )}
            </DataGrid>

            {hasNextPage && (
                <InfiniteScrollTracker
                    invisible={true}
                    onIntersect={loadNextPage}
                />
            )}
        </>
    );
}

// // // //

/**
 * GlobalAssetTableLayout
 * Renders the "All Tokenized Assets" section - the asset screener filters
 * (when showFilters is set) plus the matching assets in an infinite-scroll table
 */
export function GlobalAssetTableLayout(props: Props) {
    const { initialState, filterMetadata, stats, showFilters = false } = props;

    // API query for the asset table
    const [apiQuery, setApiQuery] = useState<ApiQuery>({
        ...DEFAULT_ASSET_LIST_QUERY,
        sort: DEFAULT_SORT,
        pagination: {
            page: 1,
            perPage: 50,
        },
    });

    // Page-local filter fields (e.g. Non KYC Only) on top of the API's metadata
    const formFilterMetadata = useMemo(
        () => [...filterMetadata, NON_KYC_FILTER_METADATA],
        [filterMetadata],
    );

    // Handles changes to the filters
    const handleFiltersChange = useCallback(
        (updatedQuery: ApiQuery) => {
            // Reset to the first page whenever a filter changes
            setApiQuery(
                normalizeFilterOperators(
                    {
                        ...updatedQuery,
                        pagination: { ...updatedQuery.pagination, page: 1 },
                    },
                    formFilterMetadata,
                ),
            );
        },
        [formFilterMetadata],
    );

    // Handles sorting from the table headers - clearing a column's sort falls
    // back to the default sort, since the API requires a sort field
    const handleSortChange = useCallback((updatedQuery: ApiQuery) => {
        setApiQuery(
            updatedQuery.sort.field
                ? updatedQuery
                : { ...updatedQuery, sort: DEFAULT_SORT },
        );
    }, []);

    return (
        <div className="flex flex-col gap-4 pt-4">
            <FilterFormContextProvider
                filterMetadata={formFilterMetadata}
                query={apiQuery}
                onChange={handleFiltersChange}
            >
                {/* NOTE - SectionHeader brings its own mb-2, so gap-1 keeps the filters 12px below it */}
                <div className="flex flex-col gap-1">
                    <SectionHeader label="All Tokenized Assets">
                        {/* NOTE - only for enterprise users now use right now (not even shown to non-enterprise users) */}
                        <FeatureFlag feature={FeatureNames.enableApiKeys}>
                            <DataExportProvider>
                                {({ isLoading, exportCsv }) => (
                                    <DownloadButton
                                        disabled={isLoading}
                                        onClick={() => {
                                            // Export every asset matching the current filters
                                            const filters: Filter[] =
                                                apiQuery.filter
                                                    ? [
                                                          ASSET_SCREENER_BASE_FILTER,
                                                          apiQuery.filter,
                                                      ]
                                                    : [
                                                          ASSET_SCREENER_BASE_FILTER,
                                                      ];

                                            exportCsv({
                                                query: {
                                                    filter: {
                                                        operator:
                                                            FilterOperators.And,
                                                        filters,
                                                    },
                                                    pagination: {
                                                        page: 1,
                                                        perPage: 10000,
                                                    },
                                                    sort: apiQuery.sort,
                                                },
                                                exportFilename:
                                                    "distributed-assets",
                                                exportTableName:
                                                    ExportTableNames.assets_v4,
                                            });
                                        }}
                                    />
                                )}
                            </DataExportProvider>
                        </FeatureFlag>
                    </SectionHeader>

                    {showFilters && (
                        <AssetScreenerFilters
                            apiQuery={apiQuery}
                            onChangeQuery={handleFiltersChange}
                            apy30DayValues={stats.apy30DayValues}
                            assetClassCounts={stats.assetClassCounts}
                            networkCounts={stats.networkCounts}
                            redemptionFrequencyCounts={
                                stats.redemptionFrequencyCounts
                            }
                        />
                    )}
                </div>

                <div className="overflow-clip rounded-lg border bg-card shadow-xs">
                    <AssetScreenerListProvider
                        apiQuery={apiQuery}
                        onChangeQuery={setApiQuery}
                        initialState={initialState}
                    >
                        {({
                            assets,
                            isFetching,
                            hasNextPage,
                            loadNextPage,
                            pagination,
                        }) => (
                            <GlobalAssetTable
                                assets={assets}
                                apiQuery={apiQuery}
                                onChangeQuery={handleSortChange}
                                fetching={isFetching}
                                hasNextPage={hasNextPage}
                                loadNextPage={loadNextPage}
                                pagination={pagination}
                            />
                        )}
                    </AssetScreenerListProvider>
                </div>
            </FilterFormContextProvider>
        </div>
    );
}
